import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationRouteNames } from '@core/presentation';
import { UsersServices } from '@modules/company-manager/users/domain';
import { NotificationsService } from '@geekbears/ngx-gb-ui-kit';
import { NavigationService } from '@core/application';
import { SessionService } from '@core/application/session.service';

import { passwordValidator, getPasswordValidationMessages } from '@utils/password.validator';

@Component({
  selector: 'app-admin-signup',
  templateUrl: './admin-signup.page.html',
  styleUrls: ['./admin-signup.page.scss'],
})
export class AdminSignUpPage implements OnInit {
  form!: FormGroup;
  isLoading = false;
  showPassword = false;
  showConfirmPassword = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private usersService: UsersServices,
    private notifications: NotificationsService,
    private navigationService: NavigationService,
    private sessionService: SessionService
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, passwordValidator()]],
      confirmPassword: ['', [Validators.required, passwordValidator()]],
    }, {
      validators: this.passwordMatchValidator
    });
  }

  getPasswordErrorMessage(controlName: string): string {
    const control = this.form?.get(controlName);
    if (!control || !control.touched || control.valid) {
      return '';
    }
    if (control.errors?.['required']) {
      return 'Please enter your password';
    }
    if (control.errors?.['passwordStrength']?.firstMessage) {
      return control.errors['passwordStrength'].firstMessage;
    }
    const msgs = getPasswordValidationMessages(control.value || '');
    return msgs.length > 0 ? msgs[0] : '';
  }

  ngOnInit(): void {
    this.sessionService.clearSession();
  }

  passwordMatchValidator(g: FormGroup) {
    return g.get('password')?.value === g.get('confirmPassword')?.value
      ? null : { 'mismatch': true };
  }

  onSubmit() {
    if (this.form.valid) {
      this.isLoading = true;
      const data = {
        email: this.form.value.email,
        password: this.form.value.password,
        confirmPassword: this.form.value.confirmPassword
      };

      this.usersService.checkEmailExist(data.email).subscribe({
        next: (res) => {
          if (res.success && res.data.exists) {
            this.isLoading = false;
            this.notifications.error(res.message || 'Email already exists', { duration: 5000, icon: 'alert-outline' });
          } else {
            // Email is available, proceed to basic info page
            localStorage.setItem('signup_email', data.email);
            localStorage.setItem('signup_password', data.password);
            localStorage.setItem('signup_confirm_password', data.confirmPassword);
            localStorage.setItem('signup_role', 'company-administrator');
            
            this.isLoading = false;
            this.router.navigate(['/authentication', AuthenticationRouteNames.adminInfo]);
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.notifications.error(err.error?.message || 'Error checking email', { duration: 5000, icon: 'alert-outline' });
        }
      });
    }
  }

  goBack() {
    this.navigationService.back();
  }
}
